import { Injectable } from '@angular/core';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';

/**
 * Caches Firestore read results (getDocs) to avoid repeated downloads during dev reloads.
 * Mirrors StorageUrlCacheService: memory + sessionStorage + in-flight deduplication.
 * Timestamps are serialized so cached JSON round-trips correctly.
 */
@Injectable({
  providedIn: 'root'
})
export class FirestoreReadCacheService {
  private readonly memory = new Map<string, unknown>();
  private readonly inflight = new Map<string, Promise<unknown>>();
  private readonly sessionPrefix = 'fsRead:';

  private static sessionKey(cacheKey: string): string {
    return cacheKey.replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  /** Recursively replace Firestore Timestamp with JSON-safe markers. */
  private deepSerialize(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }
    if (value instanceof Timestamp) {
      return { __fsTs: true, ms: value.toMillis() };
    }
    if (Array.isArray(value)) {
      return value.map(v => this.deepSerialize(v));
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(obj)) {
        out[k] = this.deepSerialize(obj[k]);
      }
      return out;
    }
    return value;
  }

  private deepDeserialize(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;
      if (obj.__fsTs === true && typeof obj.ms === 'number') {
        return Timestamp.fromMillis(obj.ms);
      }
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(obj)) {
        out[k] = this.deepDeserialize(obj[k]);
      }
      return out;
    }
    if (Array.isArray(value)) {
      return value.map(v => this.deepDeserialize(v));
    }
    return value;
  }

  /**
   * All documents in `posts` with `id` on each doc (same shape as collectionData + idField).
   * Shared by blog list and blog post routes so the second route hits cache.
   */
  getAllPosts(firestore: Firestore): Promise<Array<Record<string, unknown> & { id: string }>> {
    const key = 'collection:posts:all';
    return this.getCached(key, () =>
      getDocs(collection(firestore, 'posts')).then(snap =>
        snap.docs.map(d => ({ id: d.id, ...d.data() } as Record<string, unknown> & { id: string }))
      )
    );
  }

  private getCached<T>(cacheKey: string, fetcher: () => Promise<T>): Promise<T> {
    const mem = this.memory.get(cacheKey);
    if (mem !== undefined) {
      return Promise.resolve(mem as T);
    }

    const sKey = this.sessionPrefix + FirestoreReadCacheService.sessionKey(cacheKey);
    try {
      const raw = sessionStorage.getItem(sKey);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        const deserialized = this.deepDeserialize(parsed) as T;
        this.memory.set(cacheKey, deserialized);
        return Promise.resolve(deserialized);
      }
    } catch {
      /* ignore */
    }

    const pending = this.inflight.get(cacheKey) as Promise<T> | undefined;
    if (pending) {
      return pending;
    }

    const promise = fetcher()
      .then(data => {
        this.memory.set(cacheKey, data);
        try {
          const serialized = this.deepSerialize(data);
          sessionStorage.setItem(sKey, JSON.stringify(serialized));
        } catch {
          /* quota / private mode */
        }
        return data;
      })
      .finally(() => {
        this.inflight.delete(cacheKey);
      });

    this.inflight.set(cacheKey, promise);
    return promise;
  }
}
